// Núcleo puro de pontuação/ordenação/facetas de CRM > Relacionamento > Prioridades.
// Espelha o contrato de aawz_crm/core/relacionamento_score.py (UC-2) e
// contracts/usecases.md UC-4 / contracts/ui-components.md deste repo.
//
// Regra de robustez (risco R3 do plan.md): o backend pode, em deploy
// desalinhado ou rollback parcial, devolver o payload ANTIGO — sem
// pontuacao_total/pontuacao_breakdown/critico/dias_em_atraso/pl_total/nome.
// Todo acesso a esses campos passa por um "getter" que normaliza ausência
// para o valor neutro (0, [], false, '') para que o comparador NUNCA
// produza NaN nem lance exceção.

// Limiar a partir do qual um cliente é considerado crítico — ponto único
// de mudança (mesmo raciocínio do is_critico() do backend).
export const LIMIAR_CRITICO = 750

// Teto de pontos que os itens NÃO urgentes (relacionamento a vencer, tarefa
// em dia, oportunidade aberta) podem somar juntos. Espelha CAP_SECUNDARIO de
// aawz_crm/core/relacionamento_score.py — por desenho, um ponto a menos que
// LIMIAR_CRITICO, para que pendências não urgentes NUNCA, sozinhas, tornem
// um cliente crítico (crítico exige ao menos uma pendência urgente).
export const CAP_SECUNDARIO = LIMIAR_CRITICO - 1

// pontuacao_total normalizado: ausente/null/undefined vira 0.
const pontuacaoDe = (cliente) => {
  if (!cliente || cliente.pontuacao_total === null || cliente.pontuacao_total === undefined) {
    return 0
  }
  return Number(cliente.pontuacao_total) || 0
}

// dias_em_atraso normalizado: ausente/null/undefined vira 0.
const diasEmAtrasoDe = (cliente) => {
  if (!cliente || cliente.dias_em_atraso === null || cliente.dias_em_atraso === undefined) {
    return 0
  }
  return Number(cliente.dias_em_atraso) || 0
}

// pl_total normalizado: ausente/null/undefined vira 0.
const plTotalDe = (cliente) => {
  if (!cliente || cliente.pl_total === null || cliente.pl_total === undefined) {
    return 0
  }
  return Number(cliente.pl_total) || 0
}

// nome normalizado: ausente/null/undefined vira string vazia.
const nomeDe = (cliente) => {
  if (!cliente || !cliente.nome) {
    return ''
  }
  return String(cliente.nome)
}

// id_cliente_app normalizado: ausente/null/undefined vira 0 (desempate
// final ainda determinístico mesmo em payload degenerado).
const idClienteDe = (cliente) => {
  if (!cliente || cliente.id_cliente_app === null || cliente.id_cliente_app === undefined) {
    return 0
  }
  return Number(cliente.id_cliente_app) || 0
}

// pontuacao_breakdown normalizado: ausente vira lista vazia.
const breakdownDe = (cliente) => {
  if (!cliente || !Array.isArray(cliente.pontuacao_breakdown)) {
    return []
  }
  return cliente.pontuacao_breakdown
}

// Comparador para Array.prototype.sort — desempate determinístico na ordem
// exata do contrato [US-3]:
// pontuacao_total DESC → dias_em_atraso DESC → pl_total DESC →
// nome A-Z (localeCompare pt-BR) → id_cliente_app ASC.
export function compararPorPontuacao(a, b) {
  const diferencaPontuacao = pontuacaoDe(b) - pontuacaoDe(a)
  if (diferencaPontuacao !== 0) {
    return diferencaPontuacao
  }

  const diferencaDias = diasEmAtrasoDe(b) - diasEmAtrasoDe(a)
  if (diferencaDias !== 0) {
    return diferencaDias
  }

  const diferencaPl = plTotalDe(b) - plTotalDe(a)
  if (diferencaPl !== 0) {
    return diferencaPl
  }

  const diferencaNome = nomeDe(a).localeCompare(nomeDe(b), 'pt-BR')
  if (diferencaNome !== 0) {
    return diferencaNome
  }

  // Desempate final: garante ordem totalmente determinística mesmo quando
  // score, dias, PL e nome empatam.
  return idClienteDe(a) - idClienteDe(b)
}

// is_critico espelhado do backend — existe como função (não comparação
// solta) para o limiar ter um único ponto de mudança.
export function isCritico(pontuacaoTotal) {
  return pontuacaoTotal >= LIMIAR_CRITICO
}

// O cliente tem pelo menos um item do tipo informado no breakdown?
// Payload antigo (sem pontuacao_breakdown) => sempre false, nunca lança.
export function temTipo(cliente, tipo) {
  return breakdownDe(cliente).some((item) => item && item.tipo === tipo)
}

// Mapa de facetas dos chips de CRM > Relacionamento > Prioridades — substitui
// o filtro por _grupo exclusivo. As CHAVES reusam os slugs de contrato já
// existentes (relacionamento_atrasadas, tarefas_manuais_atrasadas,
// tarefas_criticas); só os labels são novos.
export const FACETAS = {
  // Defensivo: payload parcial pode trazer pontuacao_total mas ainda não ter
  // sido reprocessado pelo backend com o campo `critico` (deploy desalinhado,
  // rollback parcial — mesmo risco R3 do cabeçalho deste arquivo). Sem o
  // fallback isCritico(pontuacaoDe(c)), esse cliente cairia erroneamente em
  // "Demais clientes". `demais` é a negação EXATA desta condição — as duas
  // facetas continuam sendo uma partição (nunca se sobrepõem, nunca deixam
  // sobra).
  criticos: {
    label: 'Críticos',
    index: 1,
    predicado: (c) => c.critico === true || isCritico(pontuacaoDe(c)),
  },
  relacionamento_atrasadas: {
    label: 'Relacionamento atrasado',
    index: 2,
    predicado: (c) => temTipo(c, 'relacionamento_atrasado'),
  },
  tarefas_manuais_atrasadas: {
    label: 'Tarefa atrasada',
    index: 3,
    predicado: (c) => temTipo(c, 'tarefa_manual_atrasada'),
  },
  tarefas_criticas: {
    label: 'Atividade prioritária',
    index: 4,
    predicado: (c) => temTipo(c, 'atividade_prioritaria'),
  },
  demais: {
    label: 'Demais clientes',
    index: 5,
    predicado: (c) => !(c.critico === true || isCritico(pontuacaoDe(c))),
  },
}

// Conta quantos clientes casam com cada faceta => { criticos: N, ... }.
// Percorre a lista uma única vez por faceta; lista vazia devolve todas as
// chaves de FACETAS zeradas (nunca omite chave).
export function contarFacetas(clientes) {
  const clientesSeguro = Array.isArray(clientes) ? clientes : []

  return Object.keys(FACETAS).reduce((contagens, chave) => {
    const { predicado } = FACETAS[chave]
    contagens[chave] = clientesSeguro.filter((cliente) => predicado(cliente || {})).length
    return contagens
  }, {})
}
